<template>
  <div class="waivers">
    <HeaderParticipant
        :participants="participants"
        :participant="participant"
        @selectParticipants="selectParticipants"
        @changeSelect="changeSelect"
    />
    <v-row :class="{ 'mt-4': !age18 }">
      <v-col cols="12" v-if="loadingData" style="height:300px;">
        <v-skeleton-loader
          type="card-heading, image"
        ></v-skeleton-loader>
      </v-col>
      <v-col md="12" else>
        <div v-for="part in participantList" :key="part.id" class="ml-2 mr-2 mb-2">
          <v-card class="elevation-4" :style="`background:${cardBGColor}`">
            <v-card-text>
              <HeaderCard :part="part" />
              <v-row>
                <v-col md="12">
                  <v-data-table
                    :headers="headersWaivers"
                    :items="part.waivers"
                    :items-per-page="-1"
                    :no-data-text="$t('waivers.no_data_available')"
                    :footer-props="{ 
                      'items-per-page-text': $t('dataTable.rowsPerPageText'),
                      'items-per-page-all-text': $t('dataTable.rowsPerPageAllText')
                    }"
                    :headerProps="{
                      'sort-by-text': $t('dataTable.sortByText')
                    }"
                    class="elevation-1" 
                    dense
                  >
                    <template v-slot:[`item.waiverStatus`]="{ item }">
                      <template v-if="item.signaturesRequired > 1">
                        <span v-if="item.allSignaturesComplete" style="color: green;">
                          {{ $t("waivers.waiversigned") }}
                        </span>
                        <span v-else style="color: #b71c1c; font-weight: bold;">
                          {{ item.signaturesSigned }}/{{ item.signaturesRequired }} {{ $t("waivers.waiversigned") }}
                        </span>
                      </template>
                      <template v-else>
                        <span v-if="item.waiverStatus == 'Signed'" style="color: green;">{{ $tc("waivers.waiversigned") }}</span>
                        <span v-else-if="item.waiverStatus == 'Rejected'" style="color: red;">{{ $tc("waivers.waiverrejected") }}</span>
                        <span v-else>{{ $tc("waivers.waivernotsigned") }}</span>
                      </template>
                    </template>
                    <template v-slot:[`item.signatoryNames`]="{ item }">
                      <div v-if="getVisibleSignatories(item).length > 0" class="signatory-list">
                        <div
                          v-for="(sig, idx) in getVisibleSignatories(item)"
                          :key="`${sig.waiverInstanceId || sig.userId || 'u'}-${idx}`"
                          class="signatory-entry"
                        >
                          <v-icon
                            x-small
                            class="mr-1"
                            :class="getSignatoryViewIconClass(sig, item)"
                            @click.stop="openSignatoryPreview(sig, item)"
                          >
                            fa far fa-eye
                          </v-icon>
                          <span class="mr-2">{{ sig.name }}</span>
                          <v-chip
                            x-small
                            :color="(sig.waiverStatus || '') === 'Signed' ? 'green' : '#b71c1c'"
                            text-color="white"
                            class="mr-1"
                            style="height:18px; font-size:10px;"
                          >
                            {{ (sig.waiverStatus || '') === 'Signed' ? $t("waivers.waiversigned") : $t("waivers.waivernotsigned") }}
                          </v-chip>
                          <v-btn
                            v-if="(sig.waiverStatus || '') !== 'Signed' && sig.email"
                            icon
                            x-small
                            :disabled="isResendCoolingDown(sig)"
                            :loading="resendingEmail[sigResendKey(sig)]"
                            @click.stop="resendWaiverEmail(sig, item)"
                            :title="$t('waivers.resend_email')"
                          >
                            <v-icon x-small>fa fa-envelope</v-icon>
                          </v-btn>
                        </div>
                      </div>
                      <span v-else>-</span>
                    </template>
                    <template v-slot:[`item.addSignatory`]="{ item }">
                      <v-btn
                        v-if="canShowAddSignatory(item)"
                        x-small
                        :color="buttonColor"
                        class="btn"
                        @click="openAddSignatory(item)"
                      >
                        {{ $t("waivers.add_signatories") }}
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>
      </v-col>
    </v-row>
    <v-dialog v-model="signedModal" width="500">
      <v-card>
        <v-card-title class="text-h5">
          {{ $tc("waivers.title-confirmed") }}
        </v-card-title>
        <v-card-text>
          <p>
            {{ $tc("waivers.text-confirmed-1") }} <br />
            {{ $tc("waivers.text-confirmed-2") }}
          </p>
        </v-card-text>
        <v-card-actions class="confirmedOpenRsp">
          <v-spacer></v-spacer>
          <v-btn
            :color="`${buttonColor}`"
            class="btn btn_xs_rsp mt-sm-0 mt-1"
            @click="viewWaiver(itemConfirmed)"
          >
            {{ $t("waivers.open-new-tab") }}
          </v-btn>
          <v-btn
            :color="`${buttonColor}`"
            class="btn_xs_rsp mt-sm-0 mt-1"
            text
            @click="signedModal = false"
          >
            {{ $t("common.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="notSignedModal" width="600">
      <v-card>
        <v-card-title></v-card-title>
        <v-card-text>
              <WarningAlert v-if="!age18Waiver">
                  <p class="font-weight-bold" style="font-size: 20px;">{{ $t("waivers.waiver_not_signed") }}</p>
                  <p>{{ $t("waivers.message_for_adult_1", { name: participantName }) }}</p>
                  <p>{{ $t("waivers.message_for_adult_2", { name: participantName }) }}</p>
              </WarningAlert> 
              <WarningAlert v-else>
                  <p class="font-weight-bold" style="font-size: 20px;">{{ $t("waivers.waiver_not_signed") }}</p>
                  <p>{{ $t("waivers.message_for_minor_1", { nameMinor: participantName, nameSignatory: nameSigned }) }}</p>
                  <p>{{ $t("waivers.message_for_minor_2", { nameMinor: participantName, nameSignatory: nameSigned }) }}</p>
              </WarningAlert> 
        </v-card-text>
        <v-card-actions class="confirmedOpenRsp">
          <v-spacer></v-spacer>
          <v-btn
            :color="`${buttonColor}`"
            class="btn_xs_rsp mt-sm-0 mt-1"
            text
            @click="notSignedModal = false"
          >
            {{ $t("common.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="addSignatoryModal" :width="showNoAccountForm ? 900 : 650" persistent>
      <v-card>
        <v-card-title class="text-h6 pb-1">{{ $t("waivers.multiple_signatures_required") }}</v-card-title>
        <v-card-text>
          <p v-if="addSignatoryItem && addSignatoryItem.isMinor && addSignatoryOrgName" class="mt-0 mb-2 body-2">
            {{ addSignatoryOrgName }} {{ $t("waivers.requires_both_parents_signature", { minorName: addSignatoryMinorName }) }}
          </p>
          <p class="font-weight-bold mt-0 mb-3">{{ $t("waivers.event_locked_until_signed") }}</p>
          <div class="mb-4">
            <p class="subtitle-1 font-weight-bold mb-2">{{ $t("waivers.current_signatories") }}</p>
            <div v-if="currentSignatories.length > 0">
              <v-chip
                v-for="sig in currentSignatories"
                :key="sig.waiverInstanceId || `${sig.userId}-${sig.email}`"
                class="ma-1"
                :color="sig.waiverStatus === 'Signed' ? 'green' : '#b71c1c'"
                text-color="white"
                :close="sig.waiverStatus !== 'Signed'"
                @click:close="removeSignatoryFromMembershipWaiver(sig)"
              >
                {{ sig.name }}
                <span v-if="sig.isSelf" class="ml-1">{{ $t("waivers.you_label") }}</span>
                <span class="ml-1 text-caption">({{ sig.waiverStatus === 'Signed' ? $t("waivers.waiversigned") : $t("waivers.pending") }})</span>
              </v-chip>
            </div>
            <p v-else class="grey--text">{{ $t("waivers.no_signatories_assigned") }}</p>
          </div>

          <v-divider class="mb-4"></v-divider>

          <p class="subtitle-1 font-weight-bold mb-2">{{ $t("waivers.add_signatory_section") }}</p>

          <p>{{ $t("waivers.select_additional_signatory") }}</p>
          <div v-if="loadingSignatories" class="text-center my-4">
            <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
            <p class="mt-2 grey--text text-caption">{{ $t("common.loading") }}</p>
          </div>
          <div v-else-if="availableSignatories.length > 0">
            <v-btn
              v-for="sig in availableSignatories"
              :key="sig.userId"
              :color="buttonColor"
              class="btn ma-1"
              @click="selectSignatory(sig)"
              :loading="addingSignatory"
              :disabled="addingSignatory"
            >
              {{ sig.firstName }} {{ sig.lastName }}
            </v-btn>
          </div>
          <div class="mt-4">
            <p>{{ $t("waivers.or_enter_email") }}</p>
            <v-text-field
              v-model="signatoryEmail"
              :label="$t('waivers.signatory_email')"
              type="email"
              outlined
              dense
            ></v-text-field>

            <SignatoryAccountStepper
              v-if="showNoAccountForm"
              :signatory-email="signatoryEmail"
              :intro-text="$t('waivers.user_not_found_collect_info')"
              :min-adult-age="Number(addSignatoryItem ? (addSignatoryItem.adultAge || 18) : 18)"
              @created="handleCreatedSignatoryAccount"
              @cancel="resetNoAccountForm"
            />
            <v-btn
              v-else
              :color="buttonColor"
              class="btn"
              @click="sendSignatoryByEmail"
              :loading="addingSignatory"
              :disabled="addingSignatory || !signatoryEmail"
            >
              {{ $t("waivers.send_waiver_email") }}
            </v-btn>
          </div>

          <v-divider class="my-4"></v-divider>

          <p v-if="addSignatoryItem && addSignatoryItem.isMinor" class="body-2">
            {{ $t("waivers.sole_guardian_explanation", { minorName: addSignatoryMinorName }) }}
          </p>
          <v-btn
            color="warning"
            outlined
            small
            @click="certifySoleGuardian"
            :loading="addingSignatory"
            :disabled="addingSignatory"
          >
            <v-icon left small>mdi-shield-check</v-icon>
            {{ $t("registerEvent.certify_sole_guardian") }}
          </v-btn>

          <v-alert v-if="addSignatoryError" type="error" dense class="mt-2">{{ addSignatoryError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="addSignatoryModal = false">{{ $t("common.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmSoleGuardianModal" max-width="500">
      <v-card>
        <v-card-title class="headline" style="word-break: break-word;">{{ $t('registerEvent.certify_sole_guardian') }}</v-card-title>
        <v-card-text>
          {{ $t("registerEvent.confirm_sole_guardian") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="confirmSoleGuardianModal = false">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="primary" text @click="executeCertifySoleGuardian">
            {{ $t('common.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <Snack :snack="snackbar" :snackMsg="msg_snackbar" :snackCol="color_snackbar"/>
  </div>
</template>

<script>
import WaiverService from "../services/WaiverService";
import MembershipWaiverService from "@/services/MembershipWaiverService";
import Snack from "../components/notifications/Snack";
import AuthService from "../services/AuthService";
import { VCard, VCol, VRow, VIcon, VCardText, VDataTable, VSpacer,
         VSkeletonLoader, VBtn, VDialog, VCardTitle, VCardActions, VTextField,
         VChip, VDivider, VAlert, VProgressCircular } from 'vuetify/lib';
import GeneralService from "@/services/GeneralService";
import HeaderCard from "@/components/HeaderCard";
import { mapState } from "vuex";
import HeaderParticipant from "@/components/HeaderParticipant";
import WarningAlert from "@/components/alert/WarningAlert";
import SignatoryAccountStepper from "@/components/waiver/SignatoryAccountStepper.vue";
import { formatDateForHumans } from '@/helpers/date-helper';


export default {
  name: "Waivers",
  components:{
    Snack, VBtn, VDialog, VCardTitle, VCardActions,
    VCard, VCol, VRow, VIcon, VCardText, VDataTable,
    VSkeletonLoader, HeaderCard, HeaderParticipant,
    VSpacer, WarningAlert, VTextField, VChip, VDivider, VAlert,
    VProgressCircular, SignatoryAccountStepper
  },
  data() {
    return {
      itemsWaivers: [],
      itemsSignatories: [],
      dynamicMenuItems: [],
      snackName: '',
      dialogDelete: false,
      buttonColor: "#000",
      snackbar: false,
      msg_snackbar: "",
      color_snackbar: "",
      selectedSig: null,
      cardBGColor: "#f1f1f1",
      loadingData: true,
      reqSig: false,
      signedModal: false,
      itemConfirmed: null,
      bandLoadingPartSelected: false,
      participantList: [],
      notSignedModal: false,
      addSignatoryModal: false,
      addSignatoryItem: null,
      eligibleSignatories: [],
      signatoryEmail: '',
      addingSignatory: false,
      addSignatoryError: '',
      showNoAccountForm: false,
      confirmSoleGuardianModal: false,
      loadingSignatories: false,
      resendingEmail: {},
      resendCooldowns: {},
      waiverPollId: null
    };
  },
  beforeDestroy() {
    this.stopWaiverPoll();
  },
  mounted() {
    this.$emit("titlePage", "waivers.title");
    this.$emit("dynamicMenuItems", this.dynamicMenuItems);
    document.querySelectorAll(".alerttop").forEach(x => { x.setAttribute("style", "background:"+this.bgAlertColor); });
    document.querySelectorAll(".v-alert__icon").forEach( x => { x.setAttribute("style", "color: "+this.iconAlertColor)});
    document.querySelectorAll(".v-alert__border").forEach( x => { x.setAttribute("style", "color: "+this.borderAlertColor)});
  },
  created(){
    this.buttonColor = this.$store.getters.env.get('VUE_APP_BUTTON_COLOR');
    this.cardBGColor = this.$store.getters.env.get('VUE_APP_BACKGROUND_CARD_PART');
    this.borderAlertColor = this.$store.getters.env.get('VUE_APP_BORDER_ALERT_REGULAR');
    this.iconAlertColor = this.$store.getters.env.get('VUE_APP_ICON_ALERT_REGULAR');
    this.bgAlertColor = this.$store.getters.env.get('VUE_APP_BG_ALERT_REGULAR');
    this.getParticipants();
  },
  methods: {
    loadImage(url) {
      const urlImage = GeneralService.cleanImage(url, this.$store.getters.env.get('VUE_APP_IMAGE_PLACEHOLDER'));
      return urlImage;
    },
    async selectParticipants(){
      this.participantList = this.participantList.filter((el) => {
        const find = this.participant.find((ele) => { 
          return ele.userId ? el.userId == ele.userId : el.id == ele.id
        });
        return find ? true : false;
      });
      const usersId = this.participant.map((el) => { return el.userId; });
      this.getParticipantWaiversUsers(usersId);
      AuthService.setAuthParticipants(this.userStore.id, this.participant);
      this.loadingData = false;
    },
    async getParticipants(){
      if(!this.bandLoadingParticipants) {
        if(this.userStore) {
          this.bandLoadingParticipants = true;
          this.$store.dispatch('setParticipantsAction');  
        } else {
          this.$store.dispatch('setUserAction');
        }
      }
    },
    async getParticipantWaivers(userId) {
      let _data = [];
      this.reqSig = false;
      let isReq = false;
      await WaiverService.participantWaivers(userId).then(response => {
        if(response.data.success == 1){
          response.data.message.map(function(value) {
            if(value.qtSignReq == 1) isReq = true;

            if(value.waiverStatus == 2 || value.waiverStatus == 4){
              value.waiverStatus = 'Signed';
            }
            else if(value.waiverStatus == 3){
              value.waiverStatus = 'Rejected';
            }
            else{
              value.waiverStatus = 'Not Signed';
            }

            _data.push(value);
          });
        }
      });
      this.reqSig = isReq;
      this.itemsWaivers = _data;
    },
    async getParticipantWaiversUsers(usersId) {
      const data = {
        usersId
      }
      if(this.orgDefaultId) {
        data.defaultOrganizationId = this.orgDefaultId;
      }
      MembershipWaiverService.getParticipantsWaivers(data).then(response => {

          const parent = this;
          const responseList = Array.isArray(response.data) ? response.data : [];
          const mapWaiver = (value) => {
            value.createdDate = formatDateForHumans(value.createdDate, false);
            return {
              ...value,
              waiverStatus: value.waiverStatus,
              isReq: value.qtSignReq == 1 ? true : false,
              age18: parent.age18Calculate(value.birthday),
              createDate: value.createdDate
            };
          };

          const baseList = this.participant.map((el) => {
            const filter = responseList.filter((el1) => el.userId == el1.userId);
            return {
              ...el,
              waivers: filter.map(mapWaiver)
            };
          });

          // Also show waivers where the logged user is the signatory but
          // the rider is NOT in the participant list (e.g. parent signed
          // for a minor who isn't on their account-manager roster). Group
          // those leftover rows under a synthetic entry per rider so they
          // are still visible on /membershipWaivers.
          const knownIds = new Set(baseList.map(p => Number(p.userId)));
          const leftovers = {};
          responseList.forEach((value) => {
            const riderId = Number(value.userId);
            if (knownIds.has(riderId)) return;
            if (!leftovers[riderId]) {
              leftovers[riderId] = {
                userId: riderId,
                names: value.participantName || value.nameWaiver || '',
                image: require('@/assets/images/partPlaceholder.jpg'),
                waivers: []
              };
            }
            leftovers[riderId].waivers.push(mapWaiver(value));
          });

          this.participantList = baseList.concat(Object.values(leftovers));

          this.startWaiverPoll();
      })
      .catch(error => {
        this.error = error.response;
      });
    },
    async getParticipantSignatories(userId){
      let _signatories = [];
      await WaiverService.participantSignatories(userId).then(response =>{
        if(response.data.success == 1){
          response.data.message.map(function(value) {
            _signatories.push(value);
          });
        }
      });
      _signatories.forEach(x => {
        x.status = x.signature == null ? 0:1;
      });
      this.itemsSignatories = _signatories;
    },
    deleteItemConfirm(){
      WaiverService.removeSignatory(this.selectedSig.id).then(response =>{
        if(response.data.success == 1){
          if(response.data.message == true){
            this.color_snackbar = "success";
            this.msg_snackbar = this.$t("waivers.msg_success_deleted");
            this.snackbar = true;
          }else{
            this.color_snackbar = "danger";
            this.msg_snackbar = this.$t("waivers.msg_error_deleted");
            this.snackbar = true;
          }
        }
        this.dialogDelete = false;
        this.selectParticipants();
      });

    },
    deleteItem(item){
      this.dialogDelete = true;
      this.selectedSig = item;
    },
    createSignatory(idUser){
      this.$router.push({
        path: "createSignatory/"+idUser
      });
    },
    hasDocumentData(item) {
      if(item.waiverSystem == 'current' && !item.waiverLink) {
        return false;
      }

      if(item.waiverSystem == 'legacy' && (
          !item.uploadedDocument ||
          !item.uploadedDocumentName
        )){
        return false;
      }
      return true;
    },
    viewWaiver(item) {
      this.signedModal = false;
      const url = item.waiverSystem == 'current' ?
                  `${this.$store.getters.env.get('VUE_APP_LIVELAPS_HOST')}${item.waiverLink}` :
                  `${this.$store.getters.env.get('VUE_APP_ROOT')}/${item.uploadedDocument}${item.uploadedDocumentName}`;

      window.open(url, '_blank');
    },
    changeSelect(selectParts) {
      this.$store.commit('setParticipantsSelected', selectParts);
    },
    openWarning(item) {
      this.itemConfirmed = item;
        if(item.waiverStatus == "NotSigned") {
          this.notSignedModal = true;
        } else {
          this.signedModal = true;
        }
    },
    age18Calculate(_birthday) {
      const nowDay = new Date();
      const birthdayCurrent = _birthday;
      nowDay.setFullYear(nowDay.getFullYear() - 18);
      if(birthdayCurrent) {
        const birthday = new Date(birthdayCurrent);
        return birthday <= nowDay;
      }
      return false;
    },
    isMinorByAdultAge(item) {
      const adultAge = Number(item.adultAge || 18);
      const dob = item.birthday || item.participantDateOfBirth;
      if (!dob) return false;
      const today = new Date();
      const bd = new Date(dob);
      let age = today.getFullYear() - bd.getFullYear();
      const m = today.getMonth() - bd.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) age--;
      return age < adultAge;
    },
    hasSignedWaiverTemplate(item) {
      const currentUserId = Number(this.userStore?.id || 0);
      if (!currentUserId) return false;
      return (item.signatories || []).some((signatory) => (
        Number(signatory.userId || 0) === currentUserId &&
        signatory.waiverStatus === 'Signed'
      ));
    },
    canCurrentUserSignRow(item) {
      const currentUserId = Number(this.userStore?.id || 0);
      if (!currentUserId) return false;
      return (
        Number(item.signUserId || 0) === currentUserId &&
        (item.waiverStatus === 'NotSigned' || item.waiverStatus === 'Rejected')
      );
    },
    canShowAddSignatory(item) {
      if (!item || item.signaturesRequired <= 1 || item.allSignaturesComplete) return false;
      return this.canCurrentUserSignRow(item) || this.hasSignedWaiverTemplate(item);
    },
    getCurrentUserId() {
      return Number(
        this.userStore?.id ||
        this.$store?.state?.session?.userid ||
        this.$store?.state?.session?.user?.id ||
        0
      );
    },
    getCurrentUserEmail() {
      return String(
        this.userStore?.emailAddress ||
        this.$store?.state?.session?.user?.emailAddress ||
        this.userStore?.email ||
        this.$store?.state?.session?.user?.email ||
        ''
      ).toLowerCase().trim();
    },
    getVisibleSignatories(item) {
      const currentUserId = this.getCurrentUserId();
      const currentUserEmail = this.getCurrentUserEmail();
      const all = (item?.signatories || []).filter(s => s);
      const signedEmailCounts = {};
      all.forEach(s => {
        const email = String(s.email || '').toLowerCase().trim();
        if (email && (s.waiverStatus || '') === 'Signed') {
          signedEmailCounts[email] = (signedEmailCounts[email] || 0) + 1;
        }
      });
      const seen = new Set();
      const signatories = [];
      all.forEach((signatory) => {
        const name = String(signatory.name || '').trim();
        if (!name) return;
        const email = String(signatory.email || '').toLowerCase().trim();
        const isSole = email && (signedEmailCounts[email] || 0) > 1;
        const key = `${Number(signatory.waiverInstanceId || 0)}:${Number(signatory.userId || 0)}:${email}:${name.toLowerCase()}`;
        if (seen.has(key)) return;
        seen.add(key);
        signatories.push({
          ...signatory,
          name,
          isSole,
          isSelf: (currentUserId > 0 && Number(signatory.userId || 0) === currentUserId) ||
                  (currentUserEmail && email === currentUserEmail)
        });
      });

      if (signatories.length === 0 && Number(item.signaturesRequired || 1) <= 1) {
        const isMinor = this.isMinorByAdultAge(item);
        if (isMinor) {
          const firstName = String(this.userStore?.firstName || '').trim();
          const lastName = String(this.userStore?.lastName || '').trim();
          const registrarName = `${firstName} ${lastName}`.trim() || this.getCurrentUserEmail();
          if (registrarName) {
            signatories.push({
              _virtual: true,
              userId: currentUserId,
              name: registrarName,
              waiverStatus: item.waiverStatus === 'Signed' ? 'Signed' : 'NotSigned',
              isSelf: true,
              isSole: false
            });
          }
        } else {
          const participantName = String(item.nameWaiver || item.participantName || item.nameSigned || '').trim();
          if (participantName) {
            signatories.push({
              _virtual: true,
              userId: item.fkUserId || item.userId || 0,
              name: participantName,
              waiverStatus: item.waiverStatus === 'Signed' ? 'Signed' : 'NotSigned',
              isSelf: false,
              isSole: false
            });
          }
        }
      }

      return signatories;
    },
    buildSignatoryPreviewItem(signatory, item) {
      return {
        ...item,
        waiverStatus: signatory?.waiverStatus || item?.waiverStatus,
        waiverLink: this.resolveOtherSignatoryWaiverLink(signatory, item) || item?.waiverLink || null,
        signUserId: Number(signatory?.userId || item?.signUserId || 0),
        nameSigned: signatory?.name || item?.nameSigned || item?.nameSignedUP || item?.nameWaiver || "",
      };
    },
    canPreviewSignatory(signatory, item) {
      const waiverStatus = signatory?.waiverStatus || '';
      if (waiverStatus === 'NotSigned' || waiverStatus === 'Rejected') {
        return true;
      }
      return !!this.resolveOtherSignatoryWaiverLink(signatory, item);
    },
    getSignatoryViewIconClass(signatory, item) {
      const waiverStatus = signatory?.waiverStatus || '';
      if (waiverStatus === 'NotSigned' || waiverStatus === 'Rejected') {
        return 'red--text text--darken-4';
      }
      if (this.canPreviewSignatory(signatory, item)) {
        return '';
      }
      return 'grey--text text--lighten-1';
    },
    openSignatoryPreview(signatory, item) {
      if (!this.canPreviewSignatory(signatory, item)) {
        return;
      }
      this.openWarning(this.buildSignatoryPreviewItem(signatory, item));
    },
    sigResendKey(sig) {
      return `${sig.waiverInstanceId || 0}_${sig.userId || 0}`;
    },
    isResendCoolingDown(sig) {
      const key = this.sigResendKey(sig);
      return !!this.resendCooldowns[key];
    },
    async resendWaiverEmail(sig, item) {
      const key = this.sigResendKey(sig);
      if (this.resendCooldowns[key]) return;
      this.$set(this.resendingEmail, key, true);
      try {
        const payload = {
          email: sig.email,
          waiverInstanceId: sig.waiverInstanceId || null,
          membershipWaiverTemplateId: item.membershipWaiverTemplateId || null,
          userId: item.userId || null
        };
        const response = await MembershipWaiverService.resendWaiverEmail(payload);
        if (response.data && response.data.success === 1) {
          this.msg_snackbar = this.$t("waivers.email_sent_success");
          this.color_snackbar = "green";
        } else {
          this.msg_snackbar = response.data?.message || this.$t("waivers.email_sent_error");
          this.color_snackbar = "red";
        }
      } catch (e) {
        this.msg_snackbar = this.$t("waivers.email_sent_error");
        this.color_snackbar = "red";
      } finally {
        this.$set(this.resendingEmail, key, false);
        this.snackbar = true;
        this.$set(this.resendCooldowns, key, true);
        setTimeout(() => {
          this.$set(this.resendCooldowns, key, false);
        }, 5000);
      }
    },
    resolveOtherSignatoryWaiverLink(signatory, item) {
      if (!signatory) return '';
      if (signatory.waiverLink) return signatory.waiverLink;

      const signatories = item?.signatories || [];
      const byInstance = signatories.find((s) =>
        Number(s.waiverInstanceId || 0) > 0 &&
        Number(s.waiverInstanceId || 0) === Number(signatory.waiverInstanceId || -1) &&
        !!s.waiverLink
      );
      if (byInstance?.waiverLink) return byInstance.waiverLink;

      const byUser = signatories.find((s) =>
        Number(s.userId || 0) === Number(signatory.userId || -1) &&
        (s.waiverStatus || '') !== 'Rejected' &&
        !!s.waiverLink
      );
      if (byUser?.waiverLink) return byUser.waiverLink;

      if (item?.waiverLink && Number(item?.signUserId || 0) === Number(signatory.userId || -1)) {
        return item.waiverLink;
      }
      return '';
    },
    openOtherSignatoryWaiver(signatory, item) {
      const waiverLink = this.resolveOtherSignatoryWaiverLink(signatory, item);
      if (!waiverLink) {
        this.color_snackbar = "error";
        this.msg_snackbar = "Waiver link unavailable for this signatory.";
        this.snackbar = true;
        return;
      }
      const url = `${this.$store.getters.env.get('VUE_APP_LIVELAPS_HOST')}${waiverLink}`;
      window.open(url, '_blank');
    },
    async openAddSignatory(item) {
      this.addSignatoryItem = item;
      this.signatoryEmail = '';
      this.addSignatoryError = '';
      this.eligibleSignatories = [];
      this.loadingSignatories = true;
      this.resetNoAccountForm();
      this.addSignatoryModal = true;
      try {
        const response = await MembershipWaiverService.getEligibleSignatories({
          riderUserId: item.userId,
          membershipWaiverTemplateId: item.membershipWaiverTemplateId,
          includeAlreadyAssigned: true
        });
        if (response.data && response.data.success === 1) {
          this.eligibleSignatories = response.data.message || [];
        }
      } catch (e) {
        this.eligibleSignatories = [];
      }
      this.loadingSignatories = false;
    },
    translateWaiverError(errKey, item = null) {
      const key = Array.isArray(errKey) ? errKey[0] : errKey;
      if (key === "signatory_must_be_adult") {
        return this.$t("waivers.signatory_must_be_adult", { age: Number(item?.adultAge || 18) });
      }

      const translationKey = `waivers.${key}`;
      return (key && this.$te(translationKey)) ? this.$t(translationKey) : (key || "Error");
    },
    certifySoleGuardian() {
      this.confirmSoleGuardianModal = true;
    },
    async executeCertifySoleGuardian() {
      const currentUserEmail = this.getCurrentUserEmail();
      if (!currentUserEmail || !this.addSignatoryItem) {
        this.confirmSoleGuardianModal = false;
        this.color_snackbar = "error";
        this.msg_snackbar = this.$t("common.error_general");
        this.snackbar = true;
        return;
      }
      this.confirmSoleGuardianModal = false;

      if (!this.hasSignedWaiverTemplate(this.addSignatoryItem)) {
        this.addSignatoryModal = false;
        this.color_snackbar = "info";
        this.msg_snackbar = this.$t("registerEvent.certify_sole_guardian");
        this.snackbar = true;
        return;
      }

      this.addingSignatory = true;
      this.addSignatoryError = '';
      this.resetNoAccountForm();
      try {
        const response = await MembershipWaiverService.addAnotherSignatory({
          membershipWaiverTemplateId: this.addSignatoryItem.membershipWaiverTemplateId,
          riderUserId: this.addSignatoryItem.userId,
          signatoryEmail: currentUserEmail,
          isSoleGuardian: true
        });
        if (response.data && response.data.success === 1) {
          this.addSignatoryModal = false;
          this.color_snackbar = "success";
          this.msg_snackbar = this.$t("registerEvent.send_email_successfully");
          this.snackbar = true;
          this.getParticipantWaiversUsers(this.participant.map(el => el.userId));
        } else {
          const errKey = response.data?.message;
          this.addSignatoryError = this.translateWaiverError(errKey, this.addSignatoryItem);
        }
      } catch (e) {
        this.addSignatoryError = 'Error';
      }
      this.addingSignatory = false;
    },
    async selectSignatory(sig) {
      this.signatoryEmail = sig.email;
      await this.sendSignatoryByEmail();
    },
    async sendSignatoryByEmail() {
      if (!this.signatoryEmail || !this.addSignatoryItem) return;
      this.addingSignatory = true;
      this.addSignatoryError = '';
      this.resetNoAccountForm();
      try {
        const response = await MembershipWaiverService.addAnotherSignatory({
          membershipWaiverTemplateId: this.addSignatoryItem.membershipWaiverTemplateId,
          riderUserId: this.addSignatoryItem.userId,
          signatoryEmail: this.signatoryEmail
        });
        if (response.data && response.data.success === 1) {
          this.color_snackbar = "success";
          this.msg_snackbar = this.$t("waivers.email_sent_to_signatory", { name: this.signatoryEmail });
          this.snackbar = true;
          this.signatoryEmail = '';
          this.selectParticipants();
        } else {
          const errKey = response.data?.message;
          if (errKey === 'user_not_found_collect_info') {
            this.showNoAccountForm = true;
            this.addSignatoryError = '';
          } else {
            this.color_snackbar = "error";
            this.msg_snackbar = this.translateWaiverError(errKey, this.addSignatoryItem);
            this.addSignatoryError = this.msg_snackbar;
            this.snackbar = true;
          }
        }
      } catch (e) {
        this.color_snackbar = "error";
        this.msg_snackbar = 'Error';
        this.addSignatoryError = this.msg_snackbar;
        this.snackbar = true;
      }
      this.addingSignatory = false;
    },
    async handleCreatedSignatoryAccount(payload) {
      this.signatoryEmail = payload?.email || this.signatoryEmail;
      this.resetNoAccountForm();
      await this.sendSignatoryByEmail();
    },
    async removeSignatoryFromMembershipWaiver(sig) {
      if (!this.addSignatoryItem || !sig.waiverInstanceId || sig.waiverStatus === 'Signed') return;
      this.addingSignatory = true;
      this.addSignatoryError = '';
      try {
        const response = await MembershipWaiverService.removeSignatory({
          waiverInstanceId: sig.waiverInstanceId,
          riderUserId: this.addSignatoryItem.userId
        });
        if (response.data && response.data.success === 1) {
          this.color_snackbar = "success";
          this.msg_snackbar = this.$t("waivers.signatory_removed");
          this.snackbar = true;
          this.selectParticipants();
        } else {
          const errKey = response.data?.message;
          this.addSignatoryError = errKey
            ? this.translateWaiverError(errKey, this.addSignatoryItem)
            : this.$t("waivers.signatory_remove_failed");
        }
      } catch (e) {
        this.addSignatoryError = this.$t("waivers.signatory_remove_failed");
      }
      this.addingSignatory = false;
    },
    resetNoAccountForm() {
      this.showNoAccountForm = false;
    },
    startWaiverPoll() {
      this.stopWaiverPoll();
      const hasUnsigned = (this.participantList || []).some(p =>
        (p.waivers || []).some(w => w.waiverStatus !== 'Signed')
      );
      if (!hasUnsigned) return;
      this.waiverPollId = setInterval(() => {
        this.selectParticipants();
      }, 15000);
    },
    stopWaiverPoll() {
      if (this.waiverPollId) {
        clearInterval(this.waiverPollId);
        this.waiverPollId = null;
      }
    }
  },
  computed:{
    addSignatoryMinorName() {
      if (!this.addSignatoryItem) return '';
      return `${this.addSignatoryItem.riderFirstName || this.addSignatoryItem.participantName || ''} ${this.addSignatoryItem.riderLastName || this.addSignatoryItem.participantLastname || ''}`.trim();
    },
    addSignatoryOrgName() {
      if (!this.addSignatoryItem) return '';
      return this.addSignatoryItem.organizationName || this.addSignatoryItem.promoterName || '';
    },
    headersWaivers(){
      return [
        {
          text: this.$t("waivers.organizationName"),
          align: "",
          sortable: false,
          value: "promoterName"
        },
        { text: this.$t("waivers.nameOnWaiver"), value: "nameWaiver" },
        { text: this.$t("waivers.membershipNum"), value: "memberId" },
        { text: this.$t("waivers.signUpDate"), value: "createdDate" },
        { text: this.$t("waivers.uploadedWaiverStatus"), value: "waiverStatus" },
        { text: this.$t("waivers.signatures_required"), value: "sigRequired", sortable: false },
        { text: this.$t("waivers.signatories"), value: "signatoryNames", sortable: false },
        { text: '', value: "addSignatory", sortable: false },
        { text: this.$t("waivers.waivercomment"), value: "comments" }
      ];
    },
    headersSignatories(){
      return [
        {
          text: this.$t("waivers.signatory_name"),
          align: "",
          sortable: false,
          value: "firstName"
        },
        { text: this.$t("waivers.relationship"), value: "relationshipCode" },
        { text: this.$t("waivers.organizationName"), value: "organizationName" },
        { text: this.$t("waivers.uploadedWaiverStatus"), value: "status" },
        {
          text: this.$t("waivers.actions"),
          value: "actions",
          sortable: false
        }
      ]
    },
    ...mapState({
      content2(state) {
        return this.$t("common.selectParticipants.content_2", { livelapsId: state.session.userid })
      },
      age18: (state) => state.session.age18,
      userStore: (state) => state.session.user,
      participants: (state) => state.session.participants,
      participant: (state) => state.session.participantsSelected,
      orgDefaultId: state => {
        let orgIds = state.env.get('VUE_APP_DEFAULT_ORGANIZATION').map((el) => parseInt(el));
        return orgIds;
      }
    }),
    nameWaiver() {
      if(this.itemConfirmed) {
        return this.itemConfirmed.nameWaiver;
      }
      return "";
    },
    participantName(){
      if(this.itemConfirmed) {
        return this.itemConfirmed.participantName;
      }
      return "";
    },
    age18Waiver() {
      if(this.itemConfirmed) {
        return this.itemConfirmed.age18;
      }
      return true;
    },
    nameSigned() {
      if(this.itemConfirmed) {
        return this.itemConfirmed.nameSigned || this.itemConfirmed.nameSignedUP || "";
      }
      return "";
    },
    currentSignatories() {
      if (!this.addSignatoryItem) return [];
      const currentUserId = this.getCurrentUserId();
      const currentUserEmail = this.getCurrentUserEmail();
      const all = (this.addSignatoryItem.signatories || []).filter(sig => sig && (sig.waiverStatus || '') !== 'Rejected');
      const signedEmailCounts = {};
      all.forEach(s => {
        const email = String(s.email || '').toLowerCase().trim();
        if (email && (s.waiverStatus || '') === 'Signed') {
          signedEmailCounts[email] = (signedEmailCounts[email] || 0) + 1;
        }
      });
      const seen = new Set();
      return all
        .filter(sig => {
          const key = `${sig.waiverInstanceId || 0}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map(sig => {
          const email = String(sig.email || '').toLowerCase().trim();
          const isSole = email && (signedEmailCounts[email] || 0) > 1;
          return {
            ...sig,
            isSole,
            isSelf: (currentUserId > 0 && Number(sig.userId || 0) === currentUserId) ||
                    (currentUserEmail && email === currentUserEmail)
          };
        });
    },
    availableSignatories() {
      const assignedEmails = new Set(
        this.currentSignatories.map(s => String(s.email || '').toLowerCase().trim()).filter(Boolean)
      );
      const assignedUserIds = new Set(
        this.currentSignatories.map(s => Number(s.userId || 0)).filter(id => id > 0)
      );
      return this.eligibleSignatories.filter(sig => {
        if (sig.isAlreadyAssigned) return false;
        if (assignedUserIds.has(Number(sig.userId || 0))) return false;
        if (assignedEmails.has(String(sig.email || '').toLowerCase().trim())) return false;
        return true;
      });
    }
  },
  watch: {
    userStore(val) {
      if(val) {
        this.getParticipants();
      }
    },
    participant() {
      if(!this.bandLoadingPartSelected) {
        this.bandLoadingPartSelected = true;
        this.selectParticipants();
      }
    },
    participantList: {
      handler(newList) {
        if (this.addSignatoryItem && this.addSignatoryModal) {
          let refreshed = null;
          (newList || []).forEach(part => {
            if (refreshed) return;
            refreshed = (part.waivers || []).find(w =>
              Number(w.membershipWaiverTemplateId || 0) === Number(this.addSignatoryItem.membershipWaiverTemplateId || 0) &&
              Number(w.userId || 0) === Number(this.addSignatoryItem.userId || 0)
            );
          });
          if (refreshed) {
            this.addSignatoryItem = refreshed;
          }
        }
        const allDone = (newList || []).every(p =>
          (p.waivers || []).every(w => w.waiverStatus === 'Signed')
        );
        if (allDone) {
          this.stopWaiverPoll();
        }
      },
      deep: true
    }
  }
};
</script>

<style scoped>
  .btn_prim{
    color: #fff;
  }
  .partImg{
    width: 40px;
    border-radius: 50%;
    height: 40px;
  }
  .partSpan{
    color: #000;
    position: absolute;
    top: 10px;
    left: 60px;
  }
  .signatory-list{
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .signatory-entry{
    display: flex;
    align-items: center;
  }
</style>
<style>
  .waivers .v-data-table-header{
    background: #ccc;
  }
  .btn{
    color: white !important;
  }
@media (max-width: 599px) {
  .confirmedOpenRsp.v-card__actions {
        display: block;
  }
  .btn_bg, .btn_xs_rsp {
    max-width: 100%;
    min-width: auto !important;
    height: auto !important;
  }
  .btn_bg span, .btn_xs_rsp span {
    white-space: normal;
    max-width: 100%;
  }
  .lbl_search .v-text-field__details, .lbl_search .v-text-field__details .v-messages {
    min-height: 0px;
    margin-bottom: 0px !important;
  }
  .btn_bg {
    min-height: 50px;
  }
  .btn_xs_rsp {
    min-height: 36px;
  }
}
</style>
